<template>
    <v-container fluid>
        <v-card>
            <!-- Main content -->
            <v-card-text class="pa-6">
                <v-form ref="edit_form" lazy-validation>
                    <v-row>
                        <!-- Left column - Main Info -->
                        <v-col cols="12" md="6">
                            <!-- Basic Information Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-information</v-icon>
                                    {{ $t('dialog.basic_info') }}
                                    <v-spacer></v-spacer>
                                    <v-chip dark :color="display_color(currentItem.Status, product_status_list)">{{currentItem.Status | display_value(product_status_list) }}</v-chip>
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="currentItem.UID"
                                                :items="customers"
                                                item-value="ID"
                                                item-text="Username"
                                                :label="$t('form.select_customer')"
                                                autocomplete="off"
                                                @change="onCustomerChange"
                                                prepend-inner-icon="mdi-account"
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field
                                                outlined
                                                dense
                                                :label="$t('form.partner_name')"
                                                v-model="currentItem.PartnerName"
                                                prepend-inner-icon="mdi-domain"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="currentItem.StoreID"
                                                :items="storages"
                                                item-value="ID"
                                                item-text="Name"
                                                :label="$t('form.select_storage')"
                                                autocomplete="off"
                                                prepend-inner-icon="mdi-store"
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="currentItem.ProductType"
                                                :items="product_types"
                                                item-value="ID"
                                                item-text="Name"
                                                :label="$t('form.product_type')"
                                                autocomplete="off"
                                                prepend-inner-icon="mdi-shape"
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-combobox
                                                outlined
                                                dense
                                                :label="$t('form.product_name_cn')"
                                                v-model="currentItem.ProductNameCN"
                                                :items="productNameSuggestions"
                                                item-text="ProductNameCN"
                                                item-value="ProductNameCN"
                                                :loading="loadingProductNames"
                                                :disabled="!currentItem.UID"
                                                clearable
                                                autocomplete="off"
                                                :no-data-text="currentItem.UID ? 'Chưa có tên sản phẩm đã lưu' : 'Vui lòng chọn khách hàng trước'"
                                                @change="onProductNameSelected"
                                                prepend-inner-icon="mdi-package-variant"
                                            >
                                                <template v-slot:item="{ item, attrs, on }">
                                                    <v-list-item v-bind="attrs" v-on="on">
                                                        <v-list-item-content>
                                                            <v-list-item-title>{{ item.ProductNameCN }}</v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                {{ (item.Packages || []).length }} dòng chi tiết kiện hàng
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </template>
                                            </v-combobox>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <h-currency-input
                                                :label="$t('form.exchange_rate')"
                                                v-model="currentItem.ExchangeRate"
                                                :decimal="0"
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="selectedQuotes"
                                                :items="availableQuotes"
                                                item-value="ID"
                                                :item-text="quote => `${quote.ID || 'N/A'} - HSCODE: ${quote.HsCode || 'N/A'} - ${quote.ProductInfo || $t('form.no_name')}`"
                                                :label="$t('form.select_quote')"
                                                autocomplete="off"
                                                :loading="loadingQuotes"
                                                :disabled="!currentItem.UID"
                                                :filter="quoteFilter"
                                                clearable
                                                multiple
                                                chips
                                                deletable-chips
                                                small-chips
                                                :no-data-text="currentItem.UID ? $t('form.no_quote') : $t('form.select_customer_first')"
                                                prepend-inner-icon="mdi-file-document-multiple"
                                                :hint="selectedQuotes.length > 0 ? `Đã chọn ${selectedQuotes.length} khai báo` : ''"
                                                persistent-hint
                                            >
                                                <template v-slot:selection="{ item, index }">
                                                    <v-chip
                                                        v-if="index < 3"
                                                        close
                                                        small
                                                        color="primary"
                                                        text-color="white"
                                                        @click:close="removeQuote(item.ID)"
                                                    >
                                                        {{ item.ID }} - {{ (item.ProductInfo || '').substring(0, 20) }}{{ (item.ProductInfo || '').length > 20 ? '...' : '' }}
                                                    </v-chip>
                                                    <span
                                                        v-if="index === 3"
                                                        class="grey--text caption ml-2"
                                                    >
                                                        (+{{ selectedQuotes.length - 3 }} khác)
                                                    </span>
                                                </template>
                                                <template v-slot:item="{ item, attrs, on }">
                                                    <v-list-item v-bind="attrs" v-on="on">
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                {{ $t('table_headers.quote_id') }}: <strong>{{ item.ID || 'N/A' }}</strong> - {{ item.ProductInfo || $t('form.no_name') }}
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                {{ $t('form.total_amount') }}: {{ item.TotalAmount | currency }} - {{ $t('form.created_date') }}: {{ item.CreatedDate | display_date('DD/MM/YYYY') }} <span v-if="item.LinkedProductCount > 0" class="primary--text">- Đang dùng ở {{ item.LinkedProductCount }} mã hàng</span>
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                        <v-list-item-avatar v-if="item.ImageUrl">
                                                            <v-img :src="image_proxy + encodeURIComponent(item.ImageUrl) + '&w=48&h=48'" max-width="48" max-height="48"></v-img>
                                                        </v-list-item-avatar>
                                                    </v-list-item>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-text-field
                                                outlined
                                                dense
                                                :label="$t('form.note')"
                                                v-model="currentItem.Note"
                                                prepend-inner-icon="mdi-note-text"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Other Fees Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-cash-multiple</v-icon>
                                   {{ $t('dialog.fee_info') }}
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        small
                                        color="primary"
                                        @click="addFeeItem"
                                        :disabled="readOnly && !(adminRole || csRole || storeManagerRole)"
                                        class="ml-2"
                                    >
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $t('actions.add_fee') }}
                                    </v-btn>
                                </v-card-title>
                                <v-card-text class="pt-0">
                                    <v-data-table
                                        :headers="fee_headers"
                                        :items="currentFees"
                                        hide-default-footer
                                        disable-pagination
                                        class="elevation-0"
                                    >
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td class="truncate" v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{ getDisplayText(item.FeeType, feeCnyList) }}</td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.FeeType" large lazy @save="updateFeeItem">
                                                        <div>{{ getDisplayText(item.FeeType, feeCnyList) }}</div>
                                                        <template v-slot:input>
                                                            <v-select 
                                                                v-model="item.FeeType" 
                                                                :items="feeCnyList" 
                                                                item-text="name" 
                                                                item-value="value" 
                                                                label="Tên chi phí"
                                                                autofocus
                                                            ></v-select>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center" v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{ item.Currency === 'VND' ? 'VNĐ' : '¥' }}</td>
                                                <td class="text-center" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Currency" large lazy @save="updateFeeItem">
                                                        <div>{{ item.Currency === 'VND' ? 'VNĐ' : '¥' }}</div>
                                                        <template v-slot:input>
                                                            <v-select 
                                                                v-model="item.Currency" 
                                                                :items="[{text: 'VNĐ', value: 'VND'}, {text: 'Yuan (¥)', value: 'CNY'}]" 
                                                                item-text="text" 
                                                                item-value="value" 
                                                                label="Tiền tệ"
                                                                autofocus
                                                            ></v-select>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{ item.Cost | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Cost" large lazy @save="updateFeeItem">
                                                        <div>{{ item.Cost | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Cost" :autoEmit="true" :decimal="1" label="Chi phí gốc" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{ item.FeeAmount | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.FeeAmount" large lazy @save="updateFeeItem">
                                                        <div>{{ item.FeeAmount | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.FeeAmount" :autoEmit="true" :decimal="1" label="Tiền thu khách" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="truncate" v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{ item.Note }}</td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Note" large lazy @save="updateFeeItem">
                                                        <div>{{ item.Note }}</div>
                                                        <template v-slot:input>
                                                            <v-text-field v-model="item.Note" :label="$t('form.note')" autofocus></v-text-field>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center">
                                                    <table-action-buttons :actions="getFeeItemActions(index)" :stacked="false"></table-action-buttons>
                                                </td>
                                            </tr>
                                        </template>
                                        <template v-slot:body.append>
                                            <tr class="summary-row">
                                                <td class="font-weight-bold">TỔNG CHI PHÍ</td>
                                                <td class="text-center font-weight-bold">VNĐ</td>
                                                <td class="text-end font-weight-bold">{{currentItem.OtherCost | currency }}</td>
                                                <td class="text-end font-weight-bold">{{ currentItem.OtherFee | currency }}</td>
                                                <td class="text-end font-weight-bold"></td>
                                                <td class="text-end font-weight-bold"></td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>

                            <!-- Cost and Tax Information Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-calculator</v-icon>
                                    {{ $t('dialog.cost_tax_info') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12">
                                            <v-checkbox
                                                :label="$t('table_headers.is_split_tax')"
                                                v-model="currentItem.IsSplitTax"
                                                
                                            ></v-checkbox>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.weight_cost')"
                                                v-model="currentItem.WeightCost"
                                                :decimal="0"
                                                
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.volume_cost')"
                                                v-model="currentItem.VolumeCost"
                                                :decimal="0"
                                                
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.service_fee_amount')"
                                                v-model="currentItem.ServiceFeeAmount"
                                                :decimal="0"
                                                
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.import_tax_amount')"
                                                v-model="currentItem.ImportTaxAmount"
                                                :decimal="0"
                                                :disabled="!adminRole || !currentItem.IsSplitTax"
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.vat_tax_amount')"
                                                v-model="currentItem.VatTaxAmount"
                                                :decimal="0"
                                                :disabled="!adminRole || !currentItem.IsSplitTax"
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.subtotal')"
                                                v-model="currentItem.TotalAmount"
                                                :decimal="0"
                                                
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.self_fee')"
                                                v-model="currentItem.SelfFee"
                                                :decimal="0"
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-text-field
                                                outlined
                                                dense
                                                :label="$t('form.self_fee_note')"
                                                v-model="currentItem.SelfFeeNote"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.total_profit')"
                                                v-model="currentItem.TotalProfit"
                                                :decimal="0"
                                                readonly
                                            ></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <!-- Right column - Images and other info -->
                        <v-col cols="12" md="6">
                            <v-card outlined class="mb-4" v-if="adminRole">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-image-multiple</v-icon>
                                    {{ $t('form.image') }}
                                </v-card-title>
                                <v-card-text @paste="handlePaste">
                                    <input
                                        v-if="!uploading"
                                        type="file"
                                        ref="fileInput"
                                        @change="uploadFile"
                                        class="mb-3"
                                    >
                                    <v-progress-circular
                                        v-if="uploading > 0"
                                        indeterminate
                                        size="32"
                                        class="mb-3"
                                    ></v-progress-circular>
                                    
                                    <div class="d-flex flex-wrap">
                                        <div
                                            v-for="(f, index) in files"
                                            :key="f.url"
                                            class="mr-2 mb-2"
                                            style="position:relative; display:inline-block;"
                                        >
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on }">
                                                    <v-img
                                                        v-on="on"
                                                        :src="f.local === 1 ? f.url : (image_proxy + encodeURIComponent(f.url) + '&w=150&h=150')"
                                                        width="150"
                                                        height="150"
                                                        aspect-ratio="1"
                                                        class="rounded thumbnail-img"
                                                        @click="fileOpen(f)"
                                                        style="cursor:pointer; border:1px solid #eee;"
                                                    ></v-img>
                                                </template>
                                                <v-img
                                                    :src="f.local === 1 ? f.url : (image_proxy + encodeURIComponent(f.url) + '&w=300')"
                                                    max-width="300"
                                                ></v-img>
                                            </v-tooltip>
                                            <v-btn
                                                icon
                                                x-small
                                                color="error"
                                                style="position:absolute;top:4px;right:4px;z-index:2;"
                                                @click="fileRemove(index)"
                                            >
                                                <v-icon x-small>mdi-close-circle</v-icon>
                                            </v-btn>
                                            <div
                                                class="caption text-truncate mt-1"
                                                style="max-width:150px;font-size:11px;text-align:center;"
                                            >
                                                {{ f.name }}
                                            </div>
                                        </div>
                                    </div>

                                    <v-alert
                                        v-if="files.length === 0"
                                        text
                                        color="grey"
                                        class="mt-3"
                                    >
                                        <div class="text-center">
                                            <v-icon large color="grey lighten-1">mdi-image-off</v-icon>
                                            <div class="mt-2">{{ $t('form.no_images') }}</div>
                                            <div class="caption mt-1 grey--text">Bạn có thể dán ảnh từ clipboard (Ctrl+V / Cmd+V)</div>
                                        </div>
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                            <!-- Product Specifications Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-cube-outline</v-icon>
                                    {{ $t('dialog.product_specs') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6" md="4">
                                            <h-currency-input
                                                :label="$t('form.weight')"
                                                v-model="currentItem.Weight"
                                                :decimal="1"
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <h-currency-input
                                                :label="$t('form.volume')"
                                                v-model="currentItem.Volume"
                                                :decimal="3"
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <h-currency-input
                                                :label="$t('form.number_of_pack')"
                                                v-model="currentItem.NumberOfPack"
                                                :decimal="0"
                                            ></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Pricing Information Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-tag</v-icon>
                                    {{ $t('dialog.pricing_info') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="currentItem.PriceType"
                                                :items="price_types"
                                                item-value="value"
                                                item-text="name"
                                                :label="$t('form.price_type')"
                                                autocomplete="off"
                                                
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.weight_price')"
                                                v-model="currentItem.WeightPrice"
                                                :decimal="0"
                                                
                                            ></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input
                                                :label="$t('form.volume_price')"
                                                v-model="currentItem.VolumePrice"
                                                :decimal="0"
                                                
                                            ></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Package Details Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-package-variant-closed</v-icon>
                                    {{ $t('dialog.package_details') }}
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        small
                                        color="primary"
                                        @click="addSubItem"
                                        :disabled="readOnly && !(adminRole || csRole || storeManagerRole)"
                                        v-if="adminRole || csRole || storeManagerRole"
                                    >
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $t('actions.add_package') }}
                                    </v-btn>
                                </v-card-title>
                                <v-card-text class="pt-0">
                                    <v-data-table
                                        :headers="sub_headers"
                                        :items="currentPackages"
                                        hide-default-footer
                                        disable-pagination
                                        class="elevation-0"
                                    >
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td class="truncate" v-if="readOnly">{{ item.TransactionCode }}</td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.TransactionCode" large lazy @save="updateSubItem(true)">
                                                        <div>{{ item.TransactionCode }}</div>
                                                        <template v-slot:input>
                                                            <v-text-field v-model="item.TransactionCode" :label="$t('form.suffix_bill')" autofocus></v-text-field>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Length | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Length" large lazy @save="updateSubItem">
                                                        <div>{{ item.Length | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Length" :autoEmit="true" :decimal="1" :label="$t('form.length') + '(cm)'" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Width | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Width" large lazy @save="updateSubItem">
                                                        <div>{{ item.Width | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Width" :autoEmit="true" :decimal="1" :label="$t('form.width') + '(cm)'" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Height | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Height" large lazy @save="updateSubItem">
                                                        <div>{{ item.Height | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Height" :autoEmit="true" :decimal="1" :label="$t('form.height') + '(cm)'" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Weight | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Weight" large lazy @save="updateSubItem">
                                                        <div>{{ item.Weight | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Weight" :autoEmit="true" :decimal="1" :label="$t('form.weight') + '(kg)'" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.NumberOfPack | currency }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.NumberOfPack" large lazy @save="updateSubItem">
                                                        <div>{{ item.NumberOfPack | currency }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.NumberOfPack" :autoEmit="true" :decimal="0" :label="$t('form.number_of_pack')" autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center">
                                                    <table-action-buttons :actions="getSubItemActions(item, index)" :stacked="false"></table-action-buttons>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "@/configs";
import fileUploadService from '@/services/fileUploadService'
import { print_product_package_labels } from "@/commons/hpod";
import { sendErrorNotice, sendSuccessNotice } from "@/commons/utils";
import {display_color, display_value} from "@/commons/formater"
import _ from "lodash";

export default {
    name: "ProductDetail",
    data() {
        return {
            saving: false,
            image_proxy: configs.IMAGE_PROXY,
            price_types: configs.PRICE_TYPE_LIST,
            product_status_list: configs.PRODUCT_STATUS_LIST,
            sub_headers: [
                {
                    text: this.$t("form.suffix_bill"),
                    value: "TransactionCode",
                    sortable: false,
                },
                {
                    text: this.$t("form.length") + "(cm)",
                    value: "Length",
                    align: "end",
                },
                {
                    text: this.$t("form.width") + "(cm)",
                    value: "Width",
                    align: "end",
                },
                {
                    text: this.$t("form.height") + "(cm)",
                    value: "Height",
                    align: "end",
                },
                {
                    text: this.$t("form.weight") + "(kg)",
                    value: "Weight",
                    align: "end",
                },
                {
                    text: this.$t("form.number_of_pack"),
                    value: "NumberOfPack",
                    align: "end",
                },
                { text: "", value: "action", align: "center", sortable: false },
            ],
            files: [],
            uploading: 0,
            pendingUploads: [],
            currentPackages: [],
            productNameSuggestions: [],
            loadingProductNames: false,
            currentFees: [],
            availableQuotes: [],
            loadingQuotes: false,
            selectedQuotes: [],  // Array of selected quote IDs
            feeCnyList: configs.FEE_CNY_LIST,
            fee_headers: [
                {
                    text: "Tên chi phí",
                    value: "FeeName",
                    sortable: false,
                    width: "200px"
                },
                {
                    text: "Tiền tệ",
                    value: "Currency",
                    sortable: false,
                    align: "center",
                    width: "120px"
                },
                {
                    text: "Chi phí gốc",
                    value: "Cost",
                    align: "end",
                    width: "130px"
                },
                {
                    text: "Tiền thu khách",
                    value: "FeeAmount",
                    align: "end",
                    width: "160px"
                },
                {
                    text: "Ghi chú",
                    value: "Note",
                    sortable: false,
                },
                { text: "Thao tác", value: "action", align: "center", sortable: false, width: "100px" },
            ],
        };
    },
    computed: {
        ...mapState({
            currentItem: (state) => state.product.selected,
            customers: (state) => state.commons.customers,
            storages: (state) => state.storage.all.data,
            product_types: (state) => state.productType.all.data,
        }),
        adminRole() {
            return (
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Admin ||
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Accountant
            );
        },
        csRole() {
            return (
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.CustomerSupport
            );
        },
        storeManagerRole() {
            return this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.StoreManager;
        },
        roleCustomer() {
            return this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Customer;
        },
        readOnly() {
            if (this.roleCustomer) {
                return true;
            }
            return (
                this.currentItem &&
                this.currentItem.ID > 0 &&
                !(
                    this.currentItem.Status == configs.PRODUCT_STATUS.nhap_kho_tq ||
                    this.currentItem.Status == configs.PRODUCT_STATUS.cho_xep_xe
                )
                && !(this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Admin ||
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Accountant ||
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.CustomerSupport)
            );
        },
        totalOtherFees() {
            var exchangeRate = 1;
            if (this.currentItem && this.currentItem.ExchangeRate) {
                exchangeRate = this.currentItem.ExchangeRate;
            }
            return _.sumBy(this.currentFees, function(fee) {
                var amount = fee.FeeAmount || 0;
                return fee.Currency === 'VND' ? amount : amount * exchangeRate;
            }) || 0;
        },
        totalOtherCosts() {
            var exchangeRate = 1;
            if (this.currentItem && this.currentItem.ExchangeRate) {
                exchangeRate = this.currentItem.ExchangeRate;
            }
            return _.sumBy(this.currentFees, function(fee) {
                var cost = fee.Cost || 0;
                return fee.Currency === 'VND' ? cost : cost * exchangeRate;
            }) || 0;
        },

    },
    watch: {
        'currentItem.ID': {
            handler(newId, oldId) {
                if (newId !== oldId && this.currentItem) {
                    // Load selected quotes only when ID changes (new product loaded)
                    const val = this.currentItem;
                    if (val.QuoteIDs && Array.isArray(val.QuoteIDs)) {
                        this.selectedQuotes = [...val.QuoteIDs];
                    } else if (val.QuoteID) {
                        // Backward compatibility: single QuoteID
                        this.selectedQuotes = [val.QuoteID];
                    } else {
                        this.selectedQuotes = [];
                    }
                    
                    // Load images
                    if (val.Images) {
                        try {
                            this.files = JSON.parse(val.Images);
                        } catch (error) {
                            this.files = [];
                        }
                    } else {
                        this.files = [];
                    }
                    
                    // Load packages
                    if (val.Packages) {
                        try {
                            this.currentPackages = JSON.parse(val.Packages);
                        } catch (error) {
                            this.currentPackages = [];
                        }
                    }
                    
                    // Load fees
                    if (val.FeeObject) {
                        try {
                            this.currentFees = JSON.parse(val.FeeObject);
                        } catch (error) {
                            this.currentFees = [];
                        }
                    }

                    // Load quotes for customer if not loaded yet
                    if (val.UID && this.availableQuotes.length === 0) {
                        this.loadQuotesByCustomer(val.UID);
                    }
                }
            },
            immediate: true,
        },
        totalOtherFees(newVal) {
            if (this.currentItem) {
                this.currentItem.OtherFee = newVal;
            }
        },
        totalOtherCosts(newVal) {
            if (this.currentItem) {
                this.currentItem.OtherCost = newVal;
            }
        },
    },
    methods: {
        display_color,
        getDisplayText: display_value,
        getFeeItemActions(index) {
            return [
                { label: this.$t('actions.delete'), variant: 'danger-outline', hidden: !( !this.readOnly || this.adminRole || this.csRole || this.storeManagerRole), onClick: () => this.deleteFeeItem(index) }
            ];
        },
        getSubItemActions(item, index) {
            return [
                { label: this.$t('actions.print_temp'), variant: 'info', onClick: () => this.printPackageLabel(item) },
                { label: this.$t('actions.delete'), variant: 'danger-outline', hidden: this.readOnly, onClick: () => this.deleteSubItem(index) }
            ];
        },
        goBack() {
            this.$router.go(-1);
        },
        async saveItem() {
            if (!this.$refs.edit_form.validate()) {
                return;
            }

            this.saving = true;

            try {
                // Upload pending files first
                if (this.pendingUploads.length > 0) {
                    await this.uploadPendingFiles();
                }

                // Kiểm tra tỷ giá, nếu bằng 0 thì lấy tỷ giá từ cấu hình chung
                let rate = Number(this.currentItem.ExchangeRate || 0);
                if (!rate || rate === 0) {
                    const configCurrency = this.$store.state.configuration && this.$store.state.configuration.selected
                        ? Number(this.$store.state.configuration.selected.Currency || 0)
                        : 0;
                    if (configCurrency > 0) {
                        rate = configCurrency;
                        this.currentItem.ExchangeRate = configCurrency;
                    }
                }

                // Prepare data
                const data = {
                    ...this.currentItem,
                    ExchangeRate: rate,
                    Images: JSON.stringify(this.files),
                    Packages: JSON.stringify(this.currentPackages),
                    FeeObject: JSON.stringify(this.currentFees),
                    OtherFee: this.totalOtherFees,
                    OtherCost: this.totalOtherCosts,
                    QuoteIDs: JSON.stringify(this.selectedQuotes)  // Convert array to JSON string
                };

                // Save to store
                this.$store.dispatch("product/setDetail", {
                    data: data,
                    id: data.ID,
                }).then(ret => {
                    ret = ret.data ? ret.data : ret;
                    const productId = this.$route.params.id;
                    if (_.isEmpty(ret)) {
                        sendErrorNotice("Lỗi, không load được dữ liệu.");
                    } else if (!productId || productId === "new") {
                        this.$router.push({ name: 'ProductDetail', params: { id: ret.ID } });
                        this.load_detail();
                    }
                });
                // sendSuccessNotice(this.$t("notifications.save_success"));
            } catch (error) {
                console.error("Save error:", error);
                sendErrorNotice(this.$t("errors.save_failed"));
            } finally {
                this.saving = false;
            }
        },
        addSubItem() {
            let split_packages = [...this.currentPackages];
            split_packages.push({
                Length: 0,
                Width: 0,
                Height: 0,
                Weight: 0,
                NumberOfPack: 0,
            });
            this.currentItem.Packages = JSON.stringify(split_packages);
            this.currentPackages = split_packages;

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { Packages: this.currentItem.Packages },
                    id: this.currentItem.ID,
                });
            }
        },
        deleteSubItem(index) {
            let split_packages = [...this.currentPackages];
            split_packages.splice(index, 1);
            this.currentItem.Packages = JSON.stringify(split_packages);
            this.currentPackages = split_packages;

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { Packages: this.currentItem.Packages },
                    id: this.currentItem.ID,
                });
            }
        },
        updateSubItem(isAddSub = false) {
            if (isAddSub) {
                return;
            }
            this.currentItem.Packages = JSON.stringify(this.currentPackages);

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { Packages: this.currentItem.Packages },
                    id: this.currentItem.ID,
                });
            }
        },
        addFeeItem() {
            let fees = [...this.currentFees];
            fees.push({
                FeeType: 1,
                Currency: 'CNY',
                Cost: 0,
                FeeAmount: 0,
                Note: "",
            });
            this.currentItem.FeeObject = JSON.stringify(fees);
            this.currentFees = fees;

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { 
                        FeeObject: this.currentItem.FeeObject,
                        OtherFee: this.totalOtherFees,
                        OtherCost: this.totalOtherCosts
                    },
                    id: this.currentItem.ID,
                });
            }
        },
        deleteFeeItem(index) {
            let fees = [...this.currentFees];
            fees.splice(index, 1);
            this.currentItem.FeeObject = JSON.stringify(fees);
            this.currentFees = fees;

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { 
                        FeeObject: this.currentItem.FeeObject,
                        OtherFee: this.totalOtherFees,
                        OtherCost: this.totalOtherCosts
                    },
                    id: this.currentItem.ID,
                });
            }
        },
        updateFeeItem() {
            this.currentItem.FeeObject = JSON.stringify(this.currentFees);

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { 
                        FeeObject: this.currentItem.FeeObject,
                        OtherFee: this.totalOtherFees,
                        OtherCost: this.totalOtherCosts
                    },
                    id: this.currentItem.ID,
                });
            }
        },
        fileOpen(f) {
            window.open(this.image_proxy + encodeURIComponent(f.url), "blank");
        },
        async fileRemove(index) {
            if (!confirm('Bạn có chắc chắn muốn xóa ảnh này?')) {
                return;
            }

            const fileToRemove = this.files[index];

            // Delete from storage if it's not a local file
            if (fileToRemove.path) {
                try {
                    await fileUploadService.deleteFile(fileToRemove.path);
                    sendSuccessNotice('Đã xóa file trên storage');
                } catch (error) {
                    console.error("Error deleting file from storage:", error);
                    sendErrorNotice('Có lỗi khi xóa file trên storage, nhưng file đã được gỡ khỏi sản phẩm');
                }
            }

            // Remove from local array
            this.files.splice(index, 1);

            // Update database if product exists
            if (this.currentItem && this.currentItem.ID > 0) {
                this.currentItem.Images = JSON.stringify(this.files);
                try {
                    await this.$store.dispatch("product/setDetail", {
                        data: { Images: this.currentItem.Images },
                        id: this.currentItem.ID,
                    });
                    sendSuccessNotice('Đã cập nhật danh sách ảnh');
                } catch (error) {
                    console.error("Error updating product images:", error);
                    sendErrorNotice('Có lỗi khi cập nhật danh sách ảnh');
                }
            }
        },
        uploadFile(e) {
            const file = e.target.files[0];
            this.pendingUploads.push(file);
            this.files.push({
                local: 1,
                name: file.name,
                url: URL.createObjectURL(file),
            });
            this.$refs.fileInput.value = null;
        },
        handlePaste(e) {
            const items = e.clipboardData.items;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    const timestamp = new Date().getTime();
                    const fileName = `paste-${timestamp}.png`;
                    
                    // Create a File object with proper name
                    const file = new File([blob], fileName, { type: blob.type });
                    
                    this.pendingUploads.push(file);
                    this.files.push({
                        local: 1,
                        name: fileName,
                        url: URL.createObjectURL(file),
                    });
                    
                    sendSuccessNotice('Đã dán ảnh từ clipboard');
                    e.preventDefault();
                    break;
                }
            }
        },
        async uploadPendingFiles() {
            this.files = _.filter(this.files, (p) => { return p.local !== 1 });
            this.uploading = this.pendingUploads.length;

            for (let index = 0; index < this.pendingUploads.length; index++) {
                const file = this.pendingUploads[index];

                // Validate file using service
                const validation = fileUploadService.validateFile(file, ['jpg', 'jpeg', 'png', 'gif'], 10);
                if (!validation.valid) {
                    sendErrorNotice(validation.message);
                    this.uploading = this.uploading - 1;
                    continue;
                }

                try {
                    // Upload through backend proxy
                    const result = await fileUploadService.uploadSingleFile(file, 'bill');
                    if (result.success) {
                        this.files.push({
                            'name': result.data.name,
                            'url': result.data.url,
                            'path': result.data.path,
                            'size': result.data.size
                        });
                    } else {
                        sendErrorNotice(result.message);
                    }
                } catch (error) {
                    console.error('Upload error:', error);
                    sendErrorNotice('Lỗi upload file!');
                }

                this.uploading = this.uploading - 1;
            }
            
            // Clear pending uploads after all files are processed
            this.pendingUploads = [];
        },
        loadQuotesByCustomer(customerId) {
            if (!customerId) {
                this.availableQuotes = [];
                return;
            }

            this.loadingQuotes = true;
            this.$store
                .dispatch("quote/getQuotesByCustomer", customerId)
                .then((quotes) => {
                    this.availableQuotes = quotes || [];
                })
                .catch((error) => {
                    console.error("Error loading quotes:", error);
                    this.availableQuotes = [];
                })
                .finally(() => {
                    this.loadingQuotes = false;
                });
        },
        loadProductNameSuggestions(customerId) {
            if (!customerId) {
                this.productNameSuggestions = [];
                return;
            }

            this.loadingProductNames = true;
            this.$store
                .dispatch('product/getPackageTemplates', customerId)
                .then((templates) => {
                    if (String(this.currentItem.UID) === String(customerId)) {
                        this.productNameSuggestions = templates || [];
                    }
                })
                .catch((error) => {
                    console.error('Error loading product package templates:', error);
                    this.productNameSuggestions = [];
                })
                .finally(() => {
                    this.loadingProductNames = false;
                });
        },
        onProductNameSelected(selectedName) {
            const productName = typeof selectedName === 'object' && selectedName
                ? selectedName.ProductNameCN
                : selectedName;
            this.currentItem.ProductNameCN = productName || '';

            if (this.currentItem.ID > 0 || !productName) {
                return;
            }

            const normalizedName = productName.trim().toLocaleLowerCase();
            const template = this.productNameSuggestions.find(item =>
                (item.ProductNameCN || '').trim().toLocaleLowerCase() === normalizedName
            );
            if (!template) {
                return;
            }

            this.currentPackages = _.cloneDeep(template.Packages || []).map(packageItem => ({
                ...packageItem,
                NumberOfPack: 0,
            }));
            this.currentItem.Packages = JSON.stringify(this.currentPackages);
        },
        onCustomerChange() {
            this.selectedQuotes = [];
            this.productNameSuggestions = [];
            if (this.currentItem.ID === 0) {
                this.currentItem.ProductNameCN = '';
                this.currentPackages = [];
                this.currentItem.Packages = '[]';
            }
            if (this.currentItem.UID) {
                this.loadQuotesByCustomer(this.currentItem.UID);
                this.loadProductNameSuggestions(this.currentItem.UID);
            } else {
                this.availableQuotes = [];
            }
        },
        removeQuote(quoteId) {
            const index = this.selectedQuotes.indexOf(quoteId);
            if (index > -1) {
                this.selectedQuotes.splice(index, 1);
            }
        },
        printPackageLabel(packageItem) {
            const storage = (this.storages || []).find(item => item.ID === this.currentItem.StoreID)
                || configs.LOCATION_STORE.find(item => item.value === this.currentItem.StoreID)
                || {};
            const baseTrackingCode = packageItem.TransactionCode || this.currentItem.Code;

            if (!baseTrackingCode) {
                sendErrorNotice('Chưa có mã vận đơn để in tem.');
                return;
            }

            print_product_package_labels(this.currentItem, packageItem, storage);
        },
        quoteFilter(item, queryText, itemText) {
            if (!queryText) return true;

            const searchText = queryText.toLowerCase();
            const hsCode = (item.HsCode || "").toLowerCase();
            const productInfo = (item.ProductInfo || "").toLowerCase();

            return hsCode.includes(searchText) || productInfo.includes(searchText);
        },
        load_detail() {
            const productId = this.$route.params.id;
            if (productId && productId !== "new") {
                // Load existing product
                this.$store.dispatch("product/getDetail", productId);
            } else {
                // Create new product
                this.$store.commit("product/setDetail",  { data: {
                    ID: 0,
                    UID: null,
                    StoreID: null,
                    PartnerName: "",
                    QuoteID: null,
                    QuoteIDs: [],  // Initialize empty array for new product
                    ProductNameCN: "",
                    ProductType: null,
                    OtherFee: 0,
                    OtherCost: 0,
                    ExchangeRate: 0,
                    Weight: 0,
                    Volume: 0,
                    NumberOfPack: 0,
                    Note: "",
                    PriceType: null,
                    WeightPrice: 0,
                    VolumePrice: 0,
                    IsSplitTax: false,
                    WeightCost: 0,
                    VolumeCost: 0,
                    ServiceFeeAmount: 0,
                    ImportTaxAmount: 0,
                    VatTaxAmount: 0,
                    TotalAmount: 0,
                    SelfFee: 0,
                    TotalProfit: 0,
                    Images: "[]",
                    Packages: "[]",
                    FeeObject: "[]",
                }, id: 0});
            }
        }
    },
    mounted() {
        this.$root.$emit('REQUEST_ROOT_OPTION', {
            invisibleSaveButton: this.roleCustomer
        });
        // Listen to PageHeader save event
        this.$root.$on("REQUEST_SAVE_DATA", this.saveItem);
        this.$root.$on("REQUEST_REFRESH_DATA", this.load_detail);
        // Load required data
        this.$store.dispatch("commons/getAllCustomer");
        this.$store.dispatch("storage/getList");
        this.$store.dispatch("productType/getList");
        this.$store.dispatch("configuration/getDetail", 1);
        this.load_detail();
    },
    beforeDestroy() {
        this.$root.$off("REQUEST_SAVE_DATA");
        this.$root.$off("REQUEST_REFRESH_DATA");
    },
};
</script>

<style scoped>
.sticky-card {
    position: sticky;
    top: 80px;
}

.thumbnail-img {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s;
}

.thumbnail-img:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
}

</style>
